import { useEffect, useState } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, Plug, AlertTriangle, Copy } from "lucide-react";

const ERROR_HELP = {
  access_denied: {
    title: "Google blocked the sign-in (Error 403: access_denied)",
    steps: [
      "Your Google OAuth app is in 'Testing' mode, so only listed Test users can connect — and their tokens expire every 7 days (which is why the connection keeps dropping).",
      "Recommended fix: Google Cloud Console → APIs & Services → OAuth consent screen → click 'Publish app' to move it to Production. Connections then stay live permanently.",
      "Quick fix instead: on the same screen, add the mailbox you're connecting (e.g. office@theextensionworks.co.uk) under 'Test users', then try again.",
    ],
  },
  org_internal: {
    title: "Google blocked the sign-in (Error 403: org_internal)",
    steps: ["The OAuth app is restricted to accounts inside one Google Workspace organisation. In Google Cloud Console → OAuth consent screen, set 'User type' to External (or connect with an account from that organisation)."],
  },
  redirect_uri_mismatch: {
    title: "Google rejected the redirect address (Error 400: redirect_uri_mismatch)",
    steps: ["In Google Cloud Console → APIs & Services → Credentials → your OAuth client → 'Authorised redirect URIs', add the exact URI shown below, save, wait a minute, then try again."],
  },
  token_exchange_failed: {
    title: "Google accepted the sign-in but the token exchange failed",
    steps: ["This usually means the client secret on the server doesn't match the OAuth client, or the redirect URI below isn't in the client's 'Authorised redirect URIs'. Check both in Google Cloud Console → Credentials."],
  },
  state_expired: {
    title: "The sign-in link expired",
    steps: ["The connect attempt took longer than 10 minutes or the page was reloaded mid-flow. Just click 'Connect Gmail' again."],
  },
};

export function GmailConnect() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errReason, setErrReason] = useState("");
  const redirectUri = `${window.location.origin}/api/oauth/gmail/callback`;

  const load = () => api.get("/admin/gmail/status")
    .then(r => setStatus(r.data))
    .catch(() => setStatus({ connected: false, configured: true }))
    .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("gmail") === "connected") { toast.success("Gmail connected ✓"); window.history.replaceState({}, "", window.location.pathname); }
    else if (p.get("gmail") === "error") {
      setErrReason(p.get("reason") || "unknown");
      toast.error("Gmail connection failed — see the help box below for the exact fix.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line
  }, []);

  const connect = () => { window.location.href = `${api.defaults.baseURL}/oauth/gmail/login?origin=${encodeURIComponent(window.location.origin)}`; };
  const disconnect = async () => {
    if (!(await appConfirm({ title: "Disconnect Gmail?", description: "Customer emails won't be readable in the panel until you reconnect.", confirmLabel: "Disconnect", destructive: true }))) return;
    setBusy(true);
    try { await api.post("/admin/gmail/disconnect"); toast.success("Gmail disconnected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not disconnect"); }
    setBusy(false);
  };

  if (loading) return <div className="text-[#6B7280] text-sm">Loading…</div>;

  return (
    <div className="border border-[#E5E7EB] bg-white p-5" data-testid="gmail-connect">
      <div className="flex items-start gap-3 mb-3">
        <Mail className="text-[#C79A3B] mt-0.5" size={20} />
        <div>
          <div className="font-black tracking-tight">Connected email (Gmail)</div>
          <p className="text-sm text-[#6B7280]">Connect your Google account to read &amp; reply to customer emails inside the Email tab, sending from your verified addresses.</p>
        </div>
      </div>

      {status?.connected ? (
        <div data-testid="gmail-connected">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-700 mb-2">
            <CheckCircle2 size={16} /> Connected as {status.email}
          </div>
          {status.connected_at && <div className="text-xs text-[#6B7280] mb-3">Connected {new Date(status.connected_at).toLocaleString("en-GB")}{status.connected_by ? ` by ${status.connected_by}` : ""}</div>}
          {Array.isArray(status.sender_warnings) && status.sender_warnings.length > 0 && (
            <div data-testid="gmail-sender-warning" className="mb-4 border border-[#C0392B] bg-[#FDECEA] p-3 text-sm text-[#7B1E12]">
              <div className="font-black uppercase tracking-widest text-[11px] flex items-center gap-1.5 mb-1"><AlertTriangle size={14} /> Clients are receiving emails from {status.email}</div>
              <ul className="list-disc pl-4 space-y-0.5 text-xs">
                {status.sender_warnings.map(w => (
                  <li key={w.email}><b>{w.use}</b> emails are set to send from <b>{w.email}</b>, but that address isn&apos;t a verified &ldquo;Send mail as&rdquo; alias on {w.delivered_as} — Gmail delivers them as <b>{w.delivered_as}</b> instead.</li>
                ))}
              </ul>
              <div className="text-xs mt-2 leading-relaxed">
                <b>Fix (2 minutes, in the {status.email} Gmail account):</b> Gmail → ⚙ Settings → <i>See all settings</i> → <b>Accounts and Import</b> → <i>Send mail as</i> → <b>Add another email address</b> → enter the address above (untick &ldquo;Treat as an alias&rdquo; is fine) → Gmail emails a verification code to that mailbox → enter it. Refresh this page once verified.
              </div>
            </div>
          )}
          {Array.isArray(status.send_as) && status.send_as.length > 0 && (
            <div className="mb-4">
              <div className="label-eyebrow mb-1">Send-from addresses available</div>
              <div className="flex flex-wrap gap-2">
                {status.send_as.map(s => (
                  <span key={s.email} data-testid={`gmail-sendas-${s.email}`} className={`text-xs px-2 py-1 rounded-full border ${s.verified ? "border-[#E5E7EB] bg-[#F4F4F4]" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                    {s.email}{s.default ? " (default)" : ""}{!s.verified ? " · unverified" : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          <button data-testid="gmail-disconnect-btn" disabled={busy} onClick={disconnect} className="de-btn-secondary text-xs">{busy ? "…" : "Disconnect"}</button>
        </div>
      ) : (
        <div data-testid="gmail-disconnected">
          {!status?.configured && <p className="text-xs text-red-600 mb-2">Gmail isn't configured on the server yet.</p>}
          {errReason && (
            <div className="border border-amber-300 bg-amber-50 p-3 mb-3 text-xs text-amber-900" data-testid="gmail-error-help">
              <div className="flex items-center gap-1.5 font-black mb-1.5">
                <AlertTriangle size={13} /> {(ERROR_HELP[errReason] || { title: `Connection failed (${errReason})` }).title}
              </div>
              <ul className="list-disc pl-4 space-y-1">
                {(ERROR_HELP[errReason]?.steps || ["Check the OAuth client settings in Google Cloud Console (redirect URI, test users, publishing status) and try again."]).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          <button data-testid="gmail-connect-btn" disabled={!status?.configured} onClick={connect} className="de-btn-primary inline-flex items-center gap-2">
            <Plug size={16} /> Connect Gmail
          </button>
          <p className="text-[11px] text-[#6B7280] mt-2">You'll be sent to Google to grant access, then returned here.</p>
          <div className="mt-3 border-t border-[#F0EAE0] pt-3">
            <div className="label-eyebrow mb-1">Redirect URI for this environment</div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[11px] bg-[#F4F4F4] px-2 py-1 break-all" data-testid="gmail-redirect-uri">{redirectUri}</code>
              <button data-testid="gmail-copy-redirect" onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Redirect URI copied"); }}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#C79A3B] hover:text-[#A87E28]">
                <Copy size={11} /> Copy
              </button>
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-1.5">
              Must be listed under 'Authorised redirect URIs' on your Google OAuth client. Tip: while the OAuth app is in
              'Testing' mode, Google expires the connection every 7 days — publish it to Production to keep it permanently connected.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
